/* eslint-disable  react/no-unknown-property */
import Spinner from '@tcs/create-account/components/Spinner';
import { Address } from '@tcs/create-account/types/Address';
import { type Customer } from '@tcs/create-account/types/Customer';
import { getConfig } from '@tcs/create-account/utils/configService';
import { createZendeskWidget } from '@tcs/create-account/utils/zendesk';
import axios from 'axios';
import { format } from 'date-fns';
import join from 'lodash/join';
import values from 'lodash/values';
import { PureComponent } from 'react';

declare const window;
declare const ServiceModelActionTypes;

let allowDebug: boolean;
let apiKey: string;
let apiSecret: string;
let serviceHost: string;

class CreateAccountApplicationController {
  $scope = undefined;
  $location = undefined;
  modalService = undefined;
  applicationInitialiseUtility = undefined;
  actionEventDispatcher = undefined;

  constructor(
    $scope,
    $location,
    modalService,
    systemModelService,
    serviceModelConfigModelService,
    paymentsConfigModelService,
    userModelService,
    applicationInitialiseUtility,
    actionEventDispatcher
  ) {
    this.$scope = $scope;
    this.$location = $location;
    this.modalService = modalService;
    this.applicationInitialiseUtility = applicationInitialiseUtility;
    this.actionEventDispatcher = actionEventDispatcher;

    this.$scope.systemModel = systemModelService.systemModel;
    this.$scope.serviceModelConfigModel =
      serviceModelConfigModelService.serviceModelConfigModel;
    this.$scope.paymentsConfigModel =
      paymentsConfigModelService.paymentsConfigModel;
    this.$scope.userModel = userModelService.userModel;

    this.$scope.init = this.initialise.bind(this);
  }

  // Private

  initialise = (debug = false) => {
    const applicationConfig = {
      debug: allowDebug && debug,
      apiKey: apiKey,
      apiSecret: apiSecret,
      serviceHost: serviceHost,
    };

    this.applicationInitialiseUtility.initialiseApplication(applicationConfig);

    this.actionEventDispatcher.on(
      ServiceModelActionTypes.SERVICE_MODEL_ERROR,
      (event, error) => {
        if (!error) {
          return;
        }

        this.modalService.closeAllModals();
        this.modalService.showMessage({
          title: error.title || 'Error!',
          msg: error.msg,
        });
      }
    );
  };
}

class CreateAccountUIController {
  $scope = undefined;
  sevenStepNavigationUtility = undefined;
  sevenStepModelService = undefined;
  sevenStepViewModelService = undefined;

  constructor(
    $scope,
    sevenStepNavigationUtility,
    sevenStepModelService,
    sevenStepViewModelService
  ) {
    this.$scope = $scope;
    this.sevenStepNavigationUtility = sevenStepNavigationUtility;
    this.sevenStepModelService = sevenStepModelService;
    this.sevenStepViewModelService = sevenStepViewModelService;

    this.$scope.sevenStepCallbacks = {
      onSevenStepNewAccountFunded: this.onSevenStepNewAccountFunded,
      onSevenStepComplete: this.onSevenStepComplete,
      onSaveProgressComplete: this.onSaveProgressComplete,
      onNextStep: this.onNextStep,
    };
  }

  onNextStep = async () => {
    const {
      email,
      accountType,
      firstName,
      lastName,
      representativeInformation: hasRepresentative,
    } = this.sevenStepViewModelService
      .sevenStepViewModel as Partial<Customer> & {
      representativeInformation: boolean;
    };

    if (email) {
      await this._identify({
        email,
        firstName,
        lastName,
        accountType,
        hasRepresentative,
      });
    }
  };

  onSevenStepComplete = async () => {
    const {
      email,
      accountType,
      firstName,
      lastName,
      representativeInformation: hasRepresentative,
    } = this.sevenStepViewModelService
      .sevenStepViewModel as Partial<Customer> & {
      representativeInformation: boolean;
    };

    if (email) {
      await this._track('application_completed', {
        email,
        firstName,
        lastName,
        accountType,
        hasRepresentative,
        accountApplicationDate: format(new Date(), 'yyyy-MM-dd'),
      });
    }

    // When excludeFundAccount is enabled, the seven step will call this call back and skip the finished step.
    // this way we will make sure we navigate to the finished step (the fund account button will be hidden).
    if (this.sevenStepModelService.sevenStepModel.excludeFundAccount) {
      this.sevenStepNavigationUtility.goToFinished();
    }
  };

  onSaveProgressComplete = () => {
    window.location.replace('https://forgetrust.com');
  };

  onSevenStepNewAccountFunded = async () => {
    const { email } = this.sevenStepViewModelService.sevenStepViewModel;

    if (email) {
      await this._track('account_funding_intent', {
        email,
        accountFundingIntentDate: format(new Date(), 'yyyy-MM-dd'),
      });
    }
  };

  _identify = async (properties: Partial<Customer>) => {
    await this._putRequest(`identify`, properties);
  };

  _track = async (event: string, properties: Partial<Customer>) => {
    await this._putRequest(`track/${event}`, properties);
  };

  _putRequest = async (endpoint: string, properties: Partial<Customer>) => {
    const config = await getConfig();

    const host =
      process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071'
        : config.REACT_APP_BASE_PATH;

    await axios.put(`${host}/api/segment/${endpoint}`, properties, {
      headers: {},
      withCredentials: true,
    });
  };
}

export interface CreateAccountProps {
  loading: boolean;
  debug?: boolean;
  hidePromos?: boolean;
  hideAccountFeatures?: boolean;
  hideOnlineSetup?: boolean;
  hideClientInfo?: boolean;
  hideAffiliations?: boolean;
  hideBeneficiaries?: boolean;
  hideInvestments?: boolean;
  hideUSMail?: boolean;
  hideSaveProgress?: boolean;
  disableFundAccount?: boolean;
  hideFundAccountRequests?: boolean;
  hideVerifyIdentity?: boolean;
  hideChat?: boolean;
  defaultPhoneNumber?: string;
  promoCode?: string;
  stepsOrder?: string[];
  documentsUrl?: string;
  loginUrl?: string;
  apiKey?: string;
  apiSecret?: string;
  allowDebug: boolean;
  serviceHost: string;
  customer?: Customer;
  representativeCode?: string;
  preSavedViewModel?: {
    firstName: string;
    lastName: string;
    email: string;
    representativeInformation: boolean;
    representativeFirstName: string;
    representativeLastName: string;
    representativePhoneCountryCode: string;
    representativePhone: string;
    representativeEmail: string;
    representativeCompanyName: string;
    representativeCompanyPhoneCountryCode: string;
    representativeCompanyPhone: string;
    representativeAddress: Address;
  };
}

export const CreateAccountSteps: Record<string, string> = {
  GET_STARTED: 'getStarted',
  SELECT_ACCOUNT: 'selectAccount',
  YOUR_INFORMATION: 'yourInformation',
  BENEFICIRIES: 'addBeneficiaries',
  ACCOUNT_FEATURES: 'accountFeatures',
  ONLINE_SETUP: 'onlineSetup',
  TERMS: 'terms',
  FUND_ACCOUNT_REQUESTS: 'fundAccountRequests',
  REVIEW: 'review',
};

class Steps extends PureComponent<
  CreateAccountProps,
  { scriptsLoaded: boolean }
> {
  container = null;

  static defaultProps = {
    loading: true,
    debug: false,
    hidePromos: true,
    hideAccountFeatures: true,
    hideBeneficiaries: false,
    hideOnlineSetup: false,
    hideClientInfo: false,
    hideAffiliations: true,
    hideInvestments: true,
    hideUSMail: true,
    hideSaveProgress: false,
    disableFundAccount: false,
    hideFundAccountRequests: false,
    hideVerifyIdentity: false,
    hideChat: false,
    defaultPhoneNumber: String(),
    promoCode: String(),
    stepsOrder: values(CreateAccountSteps),
  };

  get isLoaded(): boolean {
    try {
      return (
        this.props.loading === false &&
        window !== undefined &&
        window.angular !== undefined
      );
    } catch {
      return false;
    }
  }

  async componentDidMount() {
    const config = await getConfig();
    const isDevelopment = process.env.NODE_ENV === 'development';
    const importExtension = isDevelopment ? '.js' : '.min.js';

    const scripts = [
      `scripts/IRA/ServiceModelConfig.js`,
      `scripts/ira-modules/ira-vendor${importExtension}`,
      `scripts/ira-modules/ira-common${importExtension}`,
      `scripts/ira-modules/ira-servicemodel${importExtension}`,
      `scripts/ira-modules/ira-fundaccount${importExtension}`,
      `scripts/ira-modules/ira-7step${importExtension}`,
    ].map((src) => {
      const script = document.createElement('script');

      script.src = src;
      script.async = false;

      document.body.appendChild(script);
      return script;
    });
    scripts[scripts.length - 1].onload = () => {
      this.setState({
        scriptsLoaded: true,
      });
    };
    const script = document.createElement('script');
    script.src = 'scripts/steps.js';
    script.async = true;
    document.body.appendChild(script);

    createZendeskWidget(this.props.hideChat, config.ZENDESK_KEY);

    [`css/styles.css`, `css/styles-7step-plugin.css`].forEach((src) => {
      const link = document.createElement('link');

      link.rel = 'stylesheet';
      link.href = src;

      document.head.appendChild(link);
    });
  }

  componentDidUpdate(_, prevState): void {
    if (prevState && prevState.scriptsLoaded) {
      return;
    }

    if (!this.isLoaded) {
      return;
    }

    apiKey = this.props.apiKey;
    apiSecret = this.props.apiSecret;
    allowDebug = this.props.allowDebug;
    serviceHost = this.props.serviceHost;

    window.angular
      .module('CreateAccountApplication', ['ira.7step'])
      .controller('CreateAccountUIController', [
        '$scope',
        'SevenStepNavigationUtility',
        'SevenStepModelService',
        'SevenStepViewModelService',
        CreateAccountUIController,
      ])
      .controller('CreateAccountApplicationController', [
        '$scope',
        '$location',
        'ModalService',
        'SystemModelService',
        'ServiceModelConfigModelService',
        'PaymentsConfigModelService',
        'UserModelService',
        'ApplicationInitialiseUtility',
        'ActionEventDispatcher',
        CreateAccountApplicationController,
      ]);

    window.angular.bootstrap(this.container, ['CreateAccountApplication']);
  }

  render = () => {
    const {
      debug,
      hidePromos,
      hideAccountFeatures,
      hideBeneficiaries,
      hideOnlineSetup,
      hideClientInfo,
      hideAffiliations,
      hideInvestments,
      hideUSMail,
      hideSaveProgress,
      disableFundAccount,
      hideFundAccountRequests,
      hideVerifyIdentity,
      defaultPhoneNumber,
      promoCode,
      stepsOrder,
      documentsUrl,
      loginUrl,
      customer,
      representativeCode = '',
      preSavedViewModel = {},
    } = this.props;

    const promoCodeAttribute = promoCode ? `promo-code="${promoCode}"` : '';

    return (
      <>
        {this.isLoaded ? (
          <div
            className="iras-a-layout-wrapper iras-a-create-account"
            id="create-account">
            <div
              ref={(c) => (this.container = c)}
              ng-controller="CreateAccountApplicationController"
              ng-init={`init(${debug})`}>
              <div ng-controller="CreateAccountUIController">
                <section className="iras-a-7steps-plugin-section-process">
                  <div className="iras-a-7steps-plugin-section-process__content">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `
                          <ira-seven-step
                           hide-promos="${hidePromos}"
                           hide-account-features="${hideAccountFeatures}"
                           hide-beneficiaries="${hideBeneficiaries}"
                           hide-online-setup="${hideOnlineSetup}"
                           hide-client-info="${hideClientInfo}"
                           hide-affiliations="${hideAffiliations}"
                           hide-investments="${hideInvestments}"
                           hide-us-mail="${hideUSMail}"
                           hide-save-progress="${hideSaveProgress}"
                           exclude-fund-account="${disableFundAccount}"
                           hide-fund-account-requests="${hideFundAccountRequests}"
                           hide-verify-identity="${hideVerifyIdentity}"
                           telephone-number="${defaultPhoneNumber}"
                           ${promoCodeAttribute}
                           callbacks="sevenStepCallbacks"
                           steps-order="${join(stepsOrder, ',')}"
                           account-documents-url="${documentsUrl}"
                           login-url="${loginUrl}"
                           representative-code="${representativeCode}"
                           ${`pre-saved-view-model='${JSON.stringify(
                             {
                               ...customer,
                               ...preSavedViewModel,
                             },
                             (_, v) => v ?? undefined
                           )}'`}
                         />
                        `,
                      }}
                    />
                  </div>
                </section>
              </div>
            </div>
          </div>
        ) : (
          <div
            style={{
              minHeight: '70vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Spinner />
          </div>
        )}
      </>
    );
  };
}

export default Steps;
